import {
  Avatar,
  Box,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import DensitySmallOutlinedIcon from '@mui/icons-material/DensitySmallOutlined';
import DensityLargeOutlinedIcon from '@mui/icons-material/DensityLargeOutlined';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import LockResetOutlinedIcon from '@mui/icons-material/LockResetOutlined';
import { useState } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { useThemeMode } from '../theme/ThemeModeProvider';
import { initials, titleCase } from '../utils/format';
import { ChangePasswordDialog } from './ChangePasswordDialog';

export function UserMenu() {
  const { user, role, logout } = useAuth();
  const { mode, toggle, density, toggleDensity } = useThemeMode();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [passwordOpen, setPasswordOpen] = useState(false);
  if (!user) return null;
  return (
    <>
      <IconButton onClick={(e) => setAnchor(e.currentTarget)} aria-label="Account" size="small">
        <Avatar sx={{ width: 32, height: 32, fontSize: '0.8rem', bgcolor: 'primary.main' }}>
          {initials(user.displayName || user.email)}
        </Avatar>
      </IconButton>
      <Menu
        anchorEl={anchor}
        open={!!anchor}
        onClose={() => setAnchor(null)}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{ sx: { width: 260 } }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle2" noWrap>
            {user.displayName}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap display="block">
            {user.email}
          </Typography>
          <Typography variant="caption" color="primary" sx={{ fontWeight: 600 }}>
            {role ? titleCase(role) : user.isPlatformAdmin ? 'Platform Admin' : '—'}
          </Typography>
        </Box>
        <Divider />
        <MenuItem
          onClick={() => {
            toggle();
            setAnchor(null);
          }}
        >
          <ListItemIcon>
            {mode === 'dark' ? (
              <LightModeOutlinedIcon fontSize="small" />
            ) : (
              <DarkModeOutlinedIcon fontSize="small" />
            )}
          </ListItemIcon>
          {mode === 'dark' ? 'Light theme' : 'Dark theme'}
        </MenuItem>
        <MenuItem
          onClick={() => {
            toggleDensity();
            setAnchor(null);
          }}
        >
          {/*
            Beside the theme toggle because it is the same KIND of setting — a
            personal comfort choice, not a brand one. A tenant sets the default;
            somebody who finds compact rows too tight fixes it here.
          */}
          <ListItemIcon>
            {density === 'COMPACT' ? (
              <DensityLargeOutlinedIcon fontSize="small" />
            ) : (
              <DensitySmallOutlinedIcon fontSize="small" />
            )}
          </ListItemIcon>
          {density === 'COMPACT' ? 'Comfortable spacing' : 'Compact spacing'}
        </MenuItem>
        <MenuItem
          onClick={() => {
            setPasswordOpen(true);
            setAnchor(null);
          }}
        >
          <ListItemIcon>
            <LockResetOutlinedIcon fontSize="small" />
          </ListItemIcon>
          Change password
        </MenuItem>
        <MenuItem onClick={logout}>
          <ListItemIcon>
            <LogoutOutlinedIcon fontSize="small" />
          </ListItemIcon>
          Sign out
        </MenuItem>
      </Menu>
      <ChangePasswordDialog open={passwordOpen} onClose={() => setPasswordOpen(false)} />
    </>
  );
}
